import { useEffect, useState, useSyncExternalStore } from "react";
import { createMaintenanceClient } from "helpers/maintenance/maintenance-client";
import type { MaintenanceClient, MaintenanceSnapshot } from "helpers/maintenance/maintenance-client";
import { regionCode } from "helpers/maintenance/maintenance-state";
import { getRegion } from "helpers/utils";

export const PRODUCT = "NIYANTRASONE";
export const SAVING_PAUSED_EVENT = "maintenance:saving-paused";

let client: MaintenanceClient | null = null;

export function maintenanceClient(): MaintenanceClient {
  if (!client) {
    client = createMaintenanceClient({
      url: process.env.REACT_APP_MAINTENANCE_STATUS_URL ?? "",
      product: PRODUCT,
      region: regionCode(getRegion()),
    });
  }
  return client;
}

const subscribe = (fn: () => void) => maintenanceClient().subscribe(fn);
const getSnapshot = () => maintenanceClient().getSnapshot();

export function useMaintenance(): MaintenanceSnapshot & { client: MaintenanceClient } {
  const snapshot = useSyncExternalStore(subscribe, getSnapshot);
  useEffect(() => {
    const c = maintenanceClient();
    c.start();
    return () => c.stop();
  }, []);
  return { ...snapshot, client: maintenanceClient() };
}

/** Server-corrected time, re-read every second while `ticking` (only while a countdown is on screen). */
export function useServerNow(ticking: boolean): number {
  const [now, setNow] = useState(() => maintenanceClient().serverNow());
  const [wasTicking, setWasTicking] = useState(ticking);
  if (wasTicking !== ticking) {
    setWasTicking(ticking);
    if (ticking) setNow(maintenanceClient().serverNow());
  }
  useEffect(() => {
    if (!ticking) return;
    const c = maintenanceClient();
    const t = setInterval(() => setNow(c.serverNow()), 1000);
    return () => clearInterval(t);
  }, [ticking]);
  return ticking ? now : maintenanceClient().serverNow();
}
